import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { toast } from "sonner";
import { calculateTitleIXCompliance, updateTitleIXCompliance, TitleIXMetrics } from "@/services/titleIXService";
import { CheckCircle, AlertTriangle, XCircle, TrendingUp, Users, DollarSign } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";

const COLORS = ['#3b82f6', '#ec4899'];

export default function TitleIXCompliance() {
  const queryClient = useQueryClient();
  const [academicYear, setAcademicYear] = useState(new Date().getFullYear().toString());
  const [formData, setFormData] = useState({
    male_enrollment: "",
    female_enrollment: "",
    male_athletes: "",
    female_athletes: "",
  });

  const { data: complianceData } = useQuery({
    queryKey: ["title_ix_compliance", academicYear],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("title_ix_compliance" as any)
        .select("*")
        .eq("academic_year", academicYear)
        .single();
      
      if (error && error.code !== "PGRST116") throw error;
      return data;
    },
  });

  const { data: revenueTotals } = useQuery({
    queryKey: ["revenue_totals", academicYear],
    queryFn: async () => {
      const { data: pools } = await supabase
        .from("revenue_sharing_pools" as any)
        .select("gender, allocated_amount")
        .eq("academic_year", academicYear);

      if (!pools) return { male: 0, female: 0 };

      const male = pools
        .filter((p: any) => p.gender === "male" || p.gender === "coed")
        .reduce((sum: number, p: any) => sum + (p.gender === "coed" ? p.allocated_amount / 2 : p.allocated_amount), 0);

      const female = pools
        .filter((p: any) => p.gender === "female" || p.gender === "coed")
        .reduce((sum: number, p: any) => sum + (p.gender === "coed" ? p.allocated_amount / 2 : p.allocated_amount), 0);

      return { male, female };
    },
  });

  const updateComplianceMutation = useMutation({
    mutationFn: async (metrics: Partial<TitleIXMetrics>) => {
      await updateTitleIXCompliance(academicYear, {
        ...metrics,
        maleRevenueSharing: revenueTotals?.male || 0,
        femaleRevenueSharing: revenueTotals?.female || 0,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["title_ix_compliance"] });
      toast.success("Title IX compliance updated");
    },
  });

  const handleUpdateMetrics = () => {
    updateComplianceMutation.mutate({
      maleEnrollment: parseInt(formData.male_enrollment),
      femaleEnrollment: parseInt(formData.female_enrollment),
      maleAthletes: parseInt(formData.male_athletes),
      femaleAthletes: parseInt(formData.female_athletes),
    });
  };

  const data = complianceData as any;
  const analysis = complianceData ? calculateTitleIXCompliance({
    academicYear,
    maleEnrollment: data.male_enrollment,
    femaleEnrollment: data.female_enrollment,
    maleRevenueSharing: data.male_revenue_sharing,
    femaleRevenueSharing: data.female_revenue_sharing,
    maleAthletes: data.male_athletes,
    femaleAthletes: data.female_athletes,
    complianceStatus: data.compliance_status,
    variancePercentage: data.variance_percentage,
  }) : null;

  const getComplianceIcon = (status: string) => {
    switch (status) {
      case "compliant":
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case "at_risk":
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case "non_compliant":
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <AlertTriangle className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const getComplianceBadge = (status: string) => {
    const variants: Record<string, "default" | "destructive" | "secondary"> = {
      compliant: "default",
      at_risk: "default",
      non_compliant: "destructive",
      under_review: "secondary",
    };
    return variants[status] || "secondary";
  };

  const enrollmentData = complianceData ? [
    { name: "Male", value: data.male_enrollment },
    { name: "Female", value: data.female_enrollment },
  ] : [];

  const revenueData = complianceData ? [
    { name: "Male", value: data.male_revenue_sharing },
    { name: "Female", value: data.female_revenue_sharing },
  ] : [];

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(value);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Title IX Compliance</h1>
          <p className="text-muted-foreground">
            Monitor gender equity in revenue sharing and athletic programs
          </p>
        </div>

        <div className="flex items-center gap-4">
          <Label htmlFor="academic_year">Academic Year:</Label>
          <Input
            id="academic_year"
            value={academicYear}
            onChange={(e) => setAcademicYear(e.target.value)}
            className="w-40"
          />
        </div>

        {complianceData && (
          <>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    {getComplianceIcon(data.compliance_status)}
                    Compliance Status
                  </CardTitle>
                  <CardDescription>Current Title IX compliance assessment</CardDescription>
                </div>
                <Badge variant={getComplianceBadge(data.compliance_status)}>
                  {data.compliance_status.replace("_", " ").toUpperCase()}
                </Badge>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Variance from Enrollment</span>
                      <span className="text-sm font-bold">
                        {data.variance_percentage?.toFixed(1)}%
                      </span>
                    </div>
                    <Progress 
                      value={Math.min(data.variance_percentage || 0, 100)} 
                      className={data.variance_percentage > 5 ? "bg-red-100" : "bg-green-100"}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {data.variance_percentage <= 5 
                        ? "✓ Within acceptable variance (<5%)"
                        : "⚠ Exceeds acceptable variance (>5%)"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Enrollment</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {(data.male_enrollment + data.female_enrollment).toLocaleString()}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {analysis?.enrollmentRatio.toFixed(1)}% female
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Athletes</CardTitle>
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {(data.male_athletes + data.female_athletes).toLocaleString()}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {analysis?.athleteRatio.toFixed(1)}% female
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Revenue Sharing</CardTitle>
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {formatCurrency(data.male_revenue_sharing + data.female_revenue_sharing)}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {analysis?.revenueRatio.toFixed(1)}% female
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Per-Athlete Avg</CardTitle>
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-xs space-y-1">
                    <div>Male: {formatCurrency(analysis?.revenuePerMaleAthlete || 0)}</div>
                    <div>Female: {formatCurrency(analysis?.revenuePerFemaleAthlete || 0)}</div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Enrollment Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={enrollmentData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(entry) => `${entry.name}: ${entry.value.toLocaleString()}`}
                        outerRadius={80}
                        dataKey="value"
                      >
                        {enrollmentData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Revenue Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={revenueData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(entry) => `${entry.name}: ${formatCurrency(entry.value)}`}
                        outerRadius={80}
                        dataKey="value"
                      >
                        {revenueData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: number) => formatCurrency(value)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {analysis && (
              <>
                {analysis.warnings.length > 0 && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>Compliance Warnings</AlertTitle>
                    <AlertDescription>
                      <ul className="list-disc list-inside space-y-1">
                        {analysis.warnings.map((warning, i) => (
                          <li key={i}>{warning}</li>
                        ))}
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}

                {analysis.recommendations.length > 0 && (
                  <Alert>
                    <TrendingUp className="h-4 w-4" />
                    <AlertTitle>Recommendations</AlertTitle>
                    <AlertDescription>
                      <ul className="list-disc list-inside space-y-1">
                        {analysis.recommendations.map((rec, i) => (
                          <li key={i}>{rec}</li>
                        ))}
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}
              </>
            )}
          </>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Update Metrics</CardTitle>
            <CardDescription>Update enrollment and athlete counts for {academicYear}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="male_enrollment">Male Enrollment</Label>
                <Input
                  id="male_enrollment"
                  type="number"
                  value={formData.male_enrollment}
                  onChange={(e) => setFormData({ ...formData, male_enrollment: e.target.value })}
                  placeholder={data?.male_enrollment?.toString() || "0"}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="female_enrollment">Female Enrollment</Label>
                <Input
                  id="female_enrollment"
                  type="number"
                  value={formData.female_enrollment}
                  onChange={(e) => setFormData({ ...formData, female_enrollment: e.target.value })}
                  placeholder={data?.female_enrollment?.toString() || "0"}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="male_athletes">Male Athletes</Label>
                <Input
                  id="male_athletes"
                  type="number"
                  value={formData.male_athletes}
                  onChange={(e) => setFormData({ ...formData, male_athletes: e.target.value })}
                  placeholder={data?.male_athletes?.toString() || "0"}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="female_athletes">Female Athletes</Label>
                <Input
                  id="female_athletes"
                  type="number"
                  value={formData.female_athletes}
                  onChange={(e) => setFormData({ ...formData, female_athletes: e.target.value })}
                  placeholder={data?.female_athletes?.toString() || "0"}
                />
              </div>
            </div>
            <Button 
              onClick={handleUpdateMetrics} 
              className="mt-4"
              disabled={updateComplianceMutation.isPending}
            >
              Update Metrics
            </Button>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
